import { W, H, CONTAINER_LEFT, CONTAINER_RIGHT, GamePhase, Button } from './types';
import { getButtons } from './renderer';

export interface InputHandlers {
  getPhase: () => GamePhase;
  getCurrentRadius: () => number;
  getDropperX: () => number;
  moveDropper: (x: number) => void;
  drop: () => void;
  pressButton: (name: string) => void;
  togglePause: () => void;
  restart: () => void;
}

function inside(b: Button, x: number, y: number): boolean {
  return x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
}

export function attachInput(canvas: HTMLCanvasElement, h: InputHandlers): void {
  const toLogical = (clientX: number, clientY: number) => {
    const r = canvas.getBoundingClientRect();
    return { x: (clientX - r.left) * (W / r.width), y: (clientY - r.top) * (H / r.height) };
  };

  const clampX = (x: number) => {
    const r = h.getCurrentRadius();
    return Math.max(CONTAINER_LEFT + r, Math.min(CONTAINER_RIGHT - r, x));
  };

  // Returns true if the press was consumed by a UI button.
  const hitButton = (x: number, y: number): boolean => {
    const phase = h.getPhase();
    const btns = getButtons(phase);
    for (const name in btns) {
      if (inside(btns[name], x, y)) { h.pressButton(name); return true; }
    }
    return false;
  };

  let pendingDrop = false;

  canvas.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    const { x, y } = toLogical(e.clientX, e.clientY);
    if (hitButton(x, y)) { pendingDrop = false; return; }
    if (h.getPhase() === 'PLAYING') {
      h.moveDropper(clampX(x));
      pendingDrop = true;
      canvas.setPointerCapture(e.pointerId);
    }
  }, { passive: false });

  canvas.addEventListener('pointermove', (e) => {
    if (h.getPhase() !== 'PLAYING') return;
    const { x } = toLogical(e.clientX, e.clientY);
    h.moveDropper(clampX(x));
  }, { passive: false });

  canvas.addEventListener('pointerup', (e) => {
    e.preventDefault();
    if (pendingDrop && h.getPhase() === 'PLAYING') h.drop();
    pendingDrop = false;
  }, { passive: false });

  canvas.addEventListener('pointercancel', () => { pendingDrop = false; });

  window.addEventListener('keydown', (e) => {
    const phase = h.getPhase();
    switch (e.key) {
      case ' ':
        if (phase === 'PLAYING') { e.preventDefault(); h.drop(); }
        break;
      case 'ArrowLeft':
        if (phase === 'PLAYING') h.moveDropper(clampX(h.getDropperX() - 20));
        break;
      case 'ArrowRight':
        if (phase === 'PLAYING') h.moveDropper(clampX(h.getDropperX() + 20));
        break;
      case 'Escape':
      case 'p':
      case 'P':
        if (phase === 'PLAYING' || phase === 'PAUSED') h.togglePause();
        break;
      case 'r':
      case 'R':
        if (phase === 'GAME_OVER') h.restart();
        break;
    }
  });
}
